import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/config";
import { Star, Heart, CalendarPlus, ThumbsUp, Lock } from "lucide-react";
import { toast } from "sonner";

/**
 * Post-Service CTA — used on Profile, Dashboard, and mirrors the email/SMS
 * design. Shows ⭐ headline + subtitle + four action buttons:
 *   Leave a Google Review · Loved It (Like) · Leave a Tip · Book Your Next Service
 *
 * iter105 — Review / Like / Tip actions are LOCKED behind a completed +
 * paid visit. If no eligible booking exists, we show a padlock state
 * explaining that these unlock after service completion.
 *
 * Props:
 *   variant: "card" (default) | "compact"
 *   bookingId: optional booking to bind against. If omitted, we scan the
 *              customer's bookings for the most-recent feedback-eligible one.
 *   className: extra classes for the outer wrapper
 */
export default function ReviewTipCTA({ variant = "card", bookingId, className = "" }) {
  const [reviewUrl, setReviewUrl] = useState("");
  const [state, setState] = useState({ eligible: false, review_status: "not_available", like_status: "not_available", tip_status: "not_available", booking_id: bookingId || null });
  const [liking, setLiking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings/public");
        if (!cancelled) setReviewUrl(data?.google_review_url || "");
      } catch (_e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Resolve the bound booking + fetch its live feedback state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let bid = bookingId;
      if (!bid) {
        try {
          const { data } = await api.get("/bookings/me");
          const list = (data?.bookings || [])
            .filter((b) => b.status === "completed" && (b.payment_status === "paid" || b.payment_status === "pay_on_arrival") && !b.duplicate_of_booking_id && !b.archived_at && b.booking_id)
            .sort((a, b) => String(b.completed_at || b.preferred_date || "").localeCompare(String(a.completed_at || a.preferred_date || "")));
          bid = list[0]?.booking_id || null;
        } catch (_e) { /* silent */ }
      }
      if (!bid) {
        if (!cancelled) setState({ eligible: false, review_status: "not_available", like_status: "not_available", tip_status: "not_available", booking_id: null });
        return;
      }
      try {
        const { data } = await api.get(`/bookings/${bid}/feedback`);
        if (!cancelled) setState({ ...data, booking_id: bid });
      } catch {
        if (!cancelled) setState({ eligible: false, review_status: "not_available", like_status: "not_available", tip_status: "not_available", booking_id: bid });
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  const eligible = state.eligible;
  const reviewHref = reviewUrl || "https://g.page/r/";
  const tipHref = eligible && state.tip_status !== "submitted" ? `/tip/${state.booking_id}` : null;
  const liked = state.like_status === "submitted";

  const doLike = async () => {
    if (!eligible || liked) return;
    setLiking(true);
    try {
      await api.post(`/bookings/${state.booking_id}/like`);
      setState((s) => ({ ...s, like_status: "submitted" }));
      toast.success("Thank you for the love! 💚");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your like");
    } finally {
      setLiking(false);
    }
  };

  const clickReview = async () => {
    if (!eligible || !state.booking_id) return;
    try { await api.post(`/reviews/click/${state.booking_id}`); } catch { /* silent */ }
  };

  // ============ Locked state — no eligible booking yet ============
  if (!eligible) {
    if (variant === "compact") {
      return (
        <div className={`rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 ${className}`}
             data-testid="review-tip-cta-locked-compact">
          <div className="flex items-center gap-2 text-[13px] font-extrabold text-gray-700 font-[Outfit]">
            <Lock className="w-4 h-4 text-gray-400" />
            Reviews & tips unlock after your first completed visit
          </div>
        </div>
      );
    }
    return (
      <div className={`rounded-3xl border border-gray-200 bg-gray-50/60 p-5 sm:p-6 ${className}`}
           data-testid="review-tip-cta-locked">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-2xl bg-gray-200 flex items-center justify-center">
            <Lock className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 font-bold">
              Available after service completion
            </div>
            <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-gray-800 font-[Outfit]">
              Reviews, Tips & Likes unlock here
            </h3>
          </div>
        </div>
        <p className="mt-3 text-[14px] text-gray-600 leading-relaxed">
          Once your visit is completed and paid, this space lights up with
          quick actions to leave a Google review, tip the crew, or show love
          for the job.
        </p>
      </div>
    );
  }

  // ============ Compact variant ============
  if (variant === "compact") {
    return (
      <div className={`rounded-2xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 ${className}`}
           data-testid="review-tip-cta-compact">
        <div className="flex items-center gap-2 text-[13px] font-extrabold text-emerald-900 font-[Outfit]">
          <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
          Leave us a Google Review
        </div>
        <p className="mt-0.5 text-[12px] text-emerald-900/80">
          Your feedback helps our small business grow.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <a href={reviewHref} target="_blank" rel="noopener noreferrer"
             onClick={clickReview}
             data-testid="cta-google-review-btn"
             className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#047857] hover:bg-[#065F46] text-white text-[12px] font-extrabold transition-colors">
            <Star className="w-3.5 h-3.5" /> Google Review
          </a>
          <button type="button" onClick={doLike} disabled={liked || liking}
                  data-testid="cta-like-btn"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-extrabold transition-colors ${liked ? "bg-rose-100 text-rose-800 cursor-default" : "bg-rose-500 hover:bg-rose-600 text-white"}`}>
            <ThumbsUp className="w-3.5 h-3.5" /> {liked ? "Loved it" : "Loved it?"}
          </button>
          {tipHref && (
            <Link to={tipHref} data-testid="cta-leave-tip-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-extrabold transition-colors">
              <Heart className="w-3.5 h-3.5" /> Leave a Tip
            </Link>
          )}
          <Link to="/book" data-testid="cta-book-next-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-900 hover:bg-black text-white text-[12px] font-extrabold transition-colors">
            <CalendarPlus className="w-3.5 h-3.5" /> Book Next
          </Link>
        </div>
      </div>
    );
  }

  // ============ Full card variant ============
  return (
    <div className={`rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40 p-5 sm:p-6 ${className}`}
         data-testid="review-tip-cta">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-2xl bg-amber-100 flex items-center justify-center">
          <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#047857] font-bold">
            Thank you for choosing JDSN
          </div>
          <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-gray-900 font-[Outfit]">
            Leave us a Google Review
          </h3>
        </div>
      </div>

      <p className="mt-3 text-[14px] text-gray-700 leading-relaxed">
        Your feedback helps our small business grow. If you loved your service, a quick review means the
        world — and you can also send your lawn professional a tip.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        <a href={reviewHref} target="_blank" rel="noopener noreferrer"
           onClick={clickReview}
           data-testid="cta-google-review-btn"
           className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#047857] hover:bg-[#065F46] text-white text-[13px] font-extrabold shadow-sm transition-colors">
          <Star className="w-4 h-4" /> Leave a Google Review
        </a>
        <button type="button" onClick={doLike} disabled={liked || liking}
                data-testid="cta-like-btn"
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[13px] font-extrabold shadow-sm transition-colors ${liked ? "bg-rose-100 text-rose-800 cursor-default" : "bg-rose-500 hover:bg-rose-600 text-white"}`}>
          <ThumbsUp className="w-4 h-4" /> {liked ? "Loved it" : "Loved it?"}
        </button>
        {tipHref ? (
          <Link to={tipHref} data-testid="cta-leave-tip-btn"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[13px] font-extrabold shadow-sm transition-colors">
            <Heart className="w-4 h-4" /> Leave a Tip
          </Link>
        ) : (
          <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-100 text-emerald-800/70 text-[13px] font-extrabold cursor-not-allowed"
                title={state.tip_status === "submitted" ? "Tip already sent — thank you!" : "Tips unlock after service completion"}
                data-testid="cta-leave-tip-disabled">
            <Heart className="w-4 h-4" /> {state.tip_status === "submitted" ? "Tip sent" : "Leave a Tip"}
          </span>
        )}
        <Link to="/book" data-testid="cta-book-next-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gray-900 hover:bg-black text-white text-[13px] font-extrabold shadow-sm transition-colors">
          <CalendarPlus className="w-4 h-4" /> Book Your Next Service
        </Link>
      </div>
    </div>
  );
}
